import { z } from 'zod';
import { ModuleTypeEnum } from './module';

/**
 * Schema for module reference info that provides 
 * basic metadata about a referenced module
 */
export const ModuleReferenceInfoSchema = z.object({
  id: z.string(), // The ID of the referenced entity
  type: z.string(), // The type of the referenced entity (e.g., PROJECT, TASK)
  name: z.string(), // Display name of the referenced entity
  identifier: z.string().optional(), // Optional secondary identifier (e.g., project code)
  path: z.string().nullable(), // URL path to navigate to the entity (null if not navigable)
  icon: z.string(), // Icon identifier to use for this entity type
  color: z.string().optional(), // Optional color code for the entity
  entity: z.object({
    id: z.string(),
    name: z.string(),
  }).nullable().optional(), // Optional entity information (for projects)
  projectType: z.string().optional(), // Optional project type (SCRUM, KANBAN, etc.) for PROJECT modules
});

export type ModuleReferenceInfo = z.infer<typeof ModuleReferenceInfoSchema>;

/**
 * Extends the existing ModuleReference type to include resolved metadata
 */
export const EnhancedModuleReferenceSchema = z.object({
  moduleType: z.nativeEnum(ModuleTypeEnum),
  moduleId: z.string(),
  moduleInfo: ModuleReferenceInfoSchema.nullable().optional(),
});

export type EnhancedModuleReference = z.infer<typeof EnhancedModuleReferenceSchema>;

/**
 * A row in the generic `module_references` table: links any module record
 * (source) to any other module record (target). This is the standard
 * cross-module reference shape going forward (ticket references, task source
 * notes, user-added links, ...).
 */
export const ModuleReferenceRecordSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  sourceModuleType: z.nativeEnum(ModuleTypeEnum),
  sourceModuleId: z.string().uuid(),
  targetModuleType: z.nativeEnum(ModuleTypeEnum),
  targetModuleId: z.string().uuid(),
  label: z.string().nullable().optional(),
  referenceKind: z.string().nullable().optional(),
  createdAt: z.union([z.string(), z.date()]).optional(),
});

export type ModuleReferenceRecord = z.infer<typeof ModuleReferenceRecordSchema>;

/** Provenance tags for system-created references. User-added rows carry null. */
export const ModuleReferenceKind = {
  /** Task extracted from a note ("Extract Action Items"). */
  EXTRACTED: 'EXTRACTED',
} as const;

/** Target of a reference being created: what the picker selected. */
export const CreateModuleReferenceTargetSchema = z.object({
  moduleType: z.nativeEnum(ModuleTypeEnum),
  moduleId: z.string().uuid(),
  label: z.string().nullable().optional(),
});

export type CreateModuleReferenceTarget = z.infer<
  typeof CreateModuleReferenceTargetSchema
>;

/**
 * Hydrated reference item returned by GET /references/:moduleType/:moduleId.
 * Direction is relative to the record the list was fetched for; moduleType /
 * moduleId describe the OTHER record (the one to render and navigate to).
 */
export interface ModuleReferenceItem {
  id: string;
  direction: 'OUTGOING' | 'INCOMING';
  moduleType: ModuleTypeEnum;
  moduleId: string;
  label: string | null;
  referenceKind: string | null;
  createdAt: string | Date;
  /** Live display name of the other record (falls back to label). */
  name: string;
  /** Human identifier when the module has one (e.g. TKT-57, task number). */
  identifier?: string | null;
  /** Status display for modules with a stage/status (tickets). */
  status?: { name: string; color: string | null } | null;
  isPrivate?: boolean;
}